// The native shell is already signed in to Firebase and holds an ID token. This backend
// accepts that same token, so the shell hands it over rather than the WebView establishing
// a second session of its own.

declare global {
  interface Window {
    __WEBVIEW_EMBEDDED__?: boolean;
    __WEBVIEW_HIDE_HEADER__?: boolean;
    __WEBVIEW_START_TRANSACTION__?: (rootPath?: string) => void;
    __WEBVIEW_PENDING_START_TRANSACTION__?: string | true;
    __WEBVIEW_AUTH_TOKEN__?: string;
    __WEBVIEW_SET_AUTH_TOKEN__?: (token: string) => void;
    __WEBVIEW_CLEAR_AUTH_TOKEN__?: () => void;
    ReactNativeWebView?: { postMessage: (message: string) => void };
  }
}

// Set by the shell before first paint. Chrome the shell already provides — its own
// navigation, most obviously — should stand down rather than compete when this is true.
export const isEmbeddedInNativeShell = (): boolean =>
  window.__WEBVIEW_EMBEDDED__ === true;

export const isWebHeaderHiddenByShell = (): boolean =>
  isEmbeddedInNativeShell() && window.__WEBVIEW_HIDE_HEADER__ === true;

let currentToken: string | null = null;
let pending: ((token: string | null) => void)[] = [];
let inFlight: Promise<string | null> | null = null;

const settle = (token: string | null) => {
  currentToken = token;
  pending.splice(0).forEach((resolve) => resolve(token));
};

if (typeof window !== 'undefined') {
  // The shell delivers the first token and every refreshed one through this.
  window.__WEBVIEW_SET_AUTH_TOKEN__ = (token: string) => settle(token);

  // The shell must call this when the native user signs out or switches account: a WebView
  // that outlives the session would otherwise keep authenticating as the previous user.
  window.__WEBVIEW_CLEAR_AUTH_TOKEN__ = () => settle(null);

  if (window.__WEBVIEW_AUTH_TOKEN__) {
    settle(window.__WEBVIEW_AUTH_TOKEN__);
    delete window.__WEBVIEW_AUTH_TOKEN__;
  }
}

const REFRESH_MARGIN_MS = 5 * 60 * 1000;
const REQUEST_TIMEOUT_MS = 5000;

// JWT payloads are base64url: `-` and `_` stand in for `+` and `/`, and the padding is
// dropped. atob() rejects both, so a perfectly good token would look undecodable.
const decodeExp = (token: string): number | null => {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.padEnd(Math.ceil(b64.length / 4) * 4, '=');
    const { exp } = JSON.parse(atob(padded));
    return typeof exp === 'number' ? exp : null;
  } catch {
    return null;
  }
};

// Read only to decide when to ask for a fresher token; the backend does the verifying.
const expiresSoon = (token: string): boolean => {
  const exp = decodeExp(token);
  return exp === null || exp * 1000 - Date.now() < REFRESH_MARGIN_MS;
};

const isExpired = (token: string): boolean => {
  const exp = decodeExp(token);
  return exp === null || exp * 1000 <= Date.now();
};

// Anything still inside the refresh margin is worth sending; anything past `exp` is not.
const usableFallback = (): string | null =>
  currentToken && !isExpired(currentToken) ? currentToken : null;

const requestFromShell = (): Promise<string | null> => {
  // One request serves every caller waiting on it, so a burst of API calls near expiry
  // does not open a bridge request and a timer each.
  if (inFlight) return inFlight;

  inFlight = new Promise<string | null>((resolve) => {
    if (!window.ReactNativeWebView) return resolve(usableFallback());

    let timer: ReturnType<typeof setTimeout>;
    const done = (token: string | null) => {
      clearTimeout(timer);
      pending = pending.filter((p) => p !== done);
      resolve(token ?? usableFallback());
    };

    pending.push(done);
    window.ReactNativeWebView.postMessage(
      JSON.stringify({ type: 'auth:token-request' })
    );
    // Fall back to whatever we hold rather than hanging a request forever.
    timer = setTimeout(() => done(null), REQUEST_TIMEOUT_MS);
  }).finally(() => {
    inFlight = null;
  });

  return inFlight;
};

// ID tokens last an hour and the WebView has no Firebase session to refresh them with,
// so the shell stays the source of truth.
export const getNativeAuthToken = async (): Promise<string | null> => {
  if (currentToken && !expiresSoon(currentToken)) return currentToken;
  return requestFromShell();
};

export const requestAppLogout = () => {
  currentToken = null;
  window.ReactNativeWebView?.postMessage(
    JSON.stringify({ type: 'auth:logout' })
  );
};
